import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';

import {
  Popup,
  I18n,
  TextInput,
  Heading,
  Icon,
  Button,
  Checkbox,
  Link,
  Preloader,
} from 'components';

import { PHONE_MASK } from 'constants/common';

import {
  formatPhone,
  cleanupPhone,
} from 'utils/formatString';

import styles from './styles.pcss';

const cx = classNames.bind(styles);

const getBalance = (props) => ((props.postpaidLimit
   && props.postpaidLimit.balance) || '').toString();

const getNotificationCtn = (props) => formatPhone((props.postpaidLimit
   && props.postpaidLimit.notificationCtn) || '');

const getDefaultState = (props) => ({
  nickName: props.nickName || '',
  isDirty: false,
  isNotificationCtnValid: true,
  isLimited: !!getBalance(props),
  spendingLimit: getBalance(props),
  notificationCtn: getNotificationCtn(props),
});

class ContractEditPopup extends PureComponent {
  constructor(props) {
    super(props);

    this.state = getDefaultState(props);
  }

  UNSAFE_componentWillReceiveProps(props) {
    this.setState(getDefaultState(props));
  }

  isSubmitDisabled() {
    const {
      isDirty,
      isNotificationCtnValid,
    } = this.state;

    return !isDirty || !isNotificationCtnValid;
  }

  saveContract() {
    const {
      ban,
      login,
      onSave,
      isPostpaid,
    } = this.props;

    const {
      nickName,
      spendingLimit,
      isLimited,
      notificationCtn,
    } = this.state;

    let postpaidLimit;
    let postpaidNotificationCtn;

    if (isPostpaid) {
      if (isLimited) {
        postpaidLimit = parseInt(spendingLimit, 10) || 0;
      } else {
        postpaidLimit = 0;
      }

      postpaidNotificationCtn = cleanupPhone(notificationCtn);
    }

    onSave({
      ban,
      nickName,
      postpaidLimit,
      postpaidNotificationCtn,
      banLogin: login,
    });
  }

  render() {
    const {
      isOpened,
      onClose,
      ban,
      isPostpaid,
      banInvoiceAddress,
      isAssociatedAccountUpdating,
    } = this.props;

    const {
      nickName,
      spendingLimit,
      notificationCtn,
      isLimited,
      isNotificationCtnValid,
    } = this.state;

    if (!ban) {
      return null;
    }

    return (
      <Popup
        className={cx('popup')}
        opened={isOpened}
        onClose={() => !isAssociatedAccountUpdating && onClose()}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();

            if (!this.isSubmitDisabled() && !isAssociatedAccountUpdating) {
              this.saveContract();
            }
          }}
        >
          <Heading
            level={3}
            className={cx('popupTitle')}
          >
            <Icon
              name="contract"
              className={cx('contractIcon')}
            />
            <I18n
              className={cx('contractTitle')}
              path="settings.numberManagement.associatedAccounts.contractTitle"
              vars={{ ban }}
            />
          </Heading>
          {isAssociatedAccountUpdating
            ? (
              <div className={cx('preloaderWrapper')}>
                <Preloader
                  className={cx('preloader')}
                  size="80"
                />
              </div>
            )
            : (
              <>
                <div className={cx('inputWrapper')}>
                  <I18n
                    className={cx('inputLabel')}
                    path="settings.numberManagement.editPopup.contractNicknameLabel"
                  />
                  <TextInput
                    className={cx('textInput')}
                    value={nickName}
                    onChange={(value) => this.setState({
                      nickName: value,
                      isDirty: true,
                    })}
                  />
                </div>
                {banInvoiceAddress
                && (
                <div className={cx('inputWrapper')}>
                  <I18n
                    className={cx('inputLabel', 'addressLabel')}
                    path="settings.numberManagement.editPopup.invoiceAddressLabel"
                  />
                  <div className={cx('invoiceAddress')}>
                    {banInvoiceAddress}
                  </div>
                </div>
                )}
                {isPostpaid
                && (
                <>
                  <div className={cx('inputWrapper')}>
                    <I18n
                      className={cx('inputLabel', 'notificationCtnLabel')}
                      path="settings.numberManagement.editPopup.notificationCtnLabel"
                    />
                    <div className={cx('notificationCtnWrapper')}>
                      <TextInput
                        className={cx('textInput', { fail: !isNotificationCtnValid })}
                        value={notificationCtn}
                        mask={PHONE_MASK}
                        onChange={(value) => value !== notificationCtn && this.setState({
                          notificationCtn: value,
                          isDirty: true,
                          isNotificationCtnValid: value.length === PHONE_MASK.length,
                        })}
                      />
                      {!isNotificationCtnValid
                        && (
                        <div className={cx('error')}>
                          <I18n path="settings.numberManagement.editPopup.invalidNotificationCtn" />
                        </div>
                        )}
                    </div>
                  </div>
                  <div className={cx('inputWrapper')}>
                    <Checkbox
                      className={cx('inputLabel')}
                      name={`limit-${ban}`}
                      checked={isLimited}
                      handleChange={() => this.setState({
                        isLimited: !isLimited,
                        isDirty: true,
                      })}
                    >
                      <I18n path="settings.numberManagement.editPopup.limitLabel" />
                    </Checkbox>
                    <TextInput
                      className={cx('spendingLimitInput')}
                      value={spendingLimit}
                      disabled={!isLimited}
                      filterInput={(value) => value.replace(/(^0+)|\D/g, '')}
                      onChange={(value) => value !== spendingLimit && this.setState({
                        spendingLimit: value,
                        isDirty: true,
                      })}
                    />
                    <I18n
                      className={cx('currency')}
                      path="settings.numberManagement.editPopup.rubles"
                    />
                  </div>
                </>
                )}
                <div className={cx('footer')}>
                  <Button
                    type="submit"
                    className={cx('saveButton')}
                    disabled={this.isSubmitDisabled()}
                  >
                    <I18n path="common.saveChanges" />
                  </Button>
                  <Link
                    onClick={onClose}
                  >
                    <I18n path="common.cancel" />
                  </Link>
                </div>
              </>
            )}
        </form>
      </Popup>
    );
  }
}

ContractEditPopup.propTypes = {
  isOpened: PropTypes.bool,
  ban: PropTypes.number,
  login: PropTypes.string,
  isPostpaid: PropTypes.bool,
  isAssociatedAccountUpdating: PropTypes.bool,
  banInvoiceAddress: PropTypes.string,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
};

export default ContractEditPopup;
